import React, { useContext, useEffect, useState } from 'react';
import { TabsProps } from 'antd';

import ActivitiesTable from '@totem/components/ticketing/details/activities/activitesTable';
import ButtonCreateActivity from '@totem/components/ticketing/details/activities/buttonCreateActivity';
import TicketContext from '@totem/components/ticketing/details/ticketContext';
import ButtonCreateTimeEntry from '@totem/components/ticketing/details/timeEntry/buttonCreateTimeEntry';
import TimeEntryTable from '@totem/components/ticketing/details/timeEntry/timeEntriesTable';
import TimeEntryDialog from '@totem/components/ticketing/details/timeEntry/timeEntryDialog';
import {
  Activity,
  Resources,
  Task,
  Tasks,
} from '@totem/components/ticketing/ticketing';
import IBTabs from '@totem/components/ui_components/antDesignOverrides/IBTabs';
import ContentPane from '@totem/components/ui_components/page/ContentPane';
import PageSectionBody from '@totem/components/ui_components/page/PageSectionBody';
import { isNotNull, isNull } from '@totem/utilities/common';
import {
  sortNumberAscending,
  sortStringAscending,
} from '@totem/utilities/tableUtilities';

type Props = {
  task: Task;
};

const TaskDetails = ({ task }: Props) => {
  const { ticket, action } = useContext(TicketContext);
  const [tasks, setTasks] = useState<Tasks>([]);
  const [resources, setResources] = useState<Resources>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [selectedTab, setSelectedTab] = useState<string>('time_entries');

  useEffect(() => {
    if (
      isNotNull(ticket) &&
      isNotNull(ticket.ticket) &&
      isNotNull(ticket.ticket.partiesInvolved)
    ) {
      const res = ticket.ticket.partiesInvolved
        .map((pi) => {
          return {
            id: pi.id,
            name: pi.name,
            email: pi.email,
          };
        })
        .sort((compA, compB) => sortStringAscending(compA.name, compB.name));
      setResources(res);
    } else {
      setResources([]);
    }
  }, [ticket]);

  useEffect(() => {
    if (
      isNotNull(ticket) &&
      isNotNull(ticket.ticket) &&
      isNotNull(ticket.ticket.tasks)
    ) {
      const tsks = ticket.ticket.tasks
        .filter((chk) => !chk.isDeleted)
        .sort((compA, compB) =>
          sortNumberAscending(compA.number, compB.number),
        );
      setTasks(tsks);
    } else {
      setTasks([]);
    }
  }, [ticket]);

  useEffect(() => {
    if (
      isNotNull(ticket) &&
      isNotNull(ticket.ticket) &&
      isNotNull(ticket.ticket.activity)
    ) {
      const taskActivities = ticket.ticket.activity
        .filter(
          (chk) =>
            isNotNull(chk.objectReference) &&
            chk.objectReference.name === 'Task' &&
            chk.objectReference.id === task.id,
        )
        .sort((compA, compB) =>
          sortStringAscending(compA.createdAt, compB.createdAt),
        );
      setActivities(taskActivities);
    } else {
      setActivities([]);
    }
  }, [ticket]);

  if (isNull(task)) {
    return <div />;
  }

  const taskTimeEntries =
    isNotNull(ticket) &&
    isNotNull(ticket.ticket) &&
    isNotNull(ticket.ticket.timeEntries)
      ? ticket.ticket.timeEntries.filter((chk) => chk.taskId === task.id)
      : [];

  const getHoursSummary = () => {
    let totalHours = 0.0;
    let billableHours = 0.0;
    let unbilledHours = 0.0;
    let nonBillableHours = 0.0;

    if (isNotNull(taskTimeEntries)) {
      for (const timeEntry of taskTimeEntries) {
        totalHours += timeEntry.hoursLogged;
        if (timeEntry.billable) {
          billableHours += timeEntry.hoursLogged;

          if (timeEntry.billingId === '') {
            unbilledHours += timeEntry.hoursLogged;
          }
        } else {
          nonBillableHours += timeEntry.hoursLogged;
        }
      }
    }

    return (
      <div style={{ fontSize: '16px', marginLeft: '48px' }}>
        <b>Total Hours:</b> {totalHours} &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{' '}
        <b>Billable Hours:</b> {billableHours}{' '}
        {unbilledHours > 0 && (
          <span style={{ color: 'red' }}>
            {'   ('}
            {unbilledHours}
            {' unbilled)'}
          </span>
        )}
        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <b>Unbillable Hours:</b>{' '}
        {nonBillableHours}
      </div>
    );
  };

  const tabItems: TabsProps['items'] = [
    {
      key: 'time_entries',
      label: 'Time Entries',
      children: (
        <>
          <div>{getHoursSummary()}</div>
          <br />
          <TimeEntryTable timeEntries={taskTimeEntries} />
        </>
      ),
    },
    {
      key: 'activities',
      label: 'Activity',
      children: (
        <>
          <ActivitiesTable activities={activities} />
        </>
      ),
    },
  ];

  const getTabExtraContent = () => {
    switch (selectedTab) {
      case 'time_entries':
        return (
          <ButtonCreateTimeEntry
            extras={{ objectReference: { name: 'Task', id: task.id } }}
          />
        );
      case 'activities':
        return (
          <ButtonCreateActivity
            extras={{ objectReference: { name: 'Task', id: task.id } }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <ContentPane>
      <PageSectionBody>
        <br />
        <div>
          <b>Description:</b> {task.description}
        </div>
        <br />
        <IBTabs
          defaultActiveKey="time_entries"
          items={tabItems}
          onChange={(newKey) => setSelectedTab(newKey)}
          tabBarExtraContent={getTabExtraContent()}
        />
      </PageSectionBody>
      {action === 'show-time-entry-create' &&
        isNotNull(ticket) &&
        isNotNull(ticket.ticket) && (
          <TimeEntryDialog
            resources={resources}
            tasks={isNotNull(tasks) ? tasks : []}
          />
        )}
    </ContentPane>
  );
};

export default TaskDetails;
